import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { canManageLicences, daysAgoText, webSignInGate, type WebSignInGate } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { Loading, PoweredBy } from '../components/States.tsx';
import type { Tenant } from '../tenant/tenant.ts';
import { LicencesScreen } from './LicencesScreen.tsx';
import { webCheckin } from './api.ts';

export interface LicenceGateProps {
  readonly sb: SupabaseClient;
  readonly tenant: Tenant;
  readonly tenantId: string;
  readonly tenantName: string;
  /** The person's roles in this organisation: tenant admins can release a seat from the refusal. */
  readonly roles: readonly string[];
  readonly onSignOut: () => void;
  readonly onSwitch?: () => void;
  readonly children: ReactNode;
}

/**
 * The seat check at sign-in (licences spec §6.4; decisions 290, 296, 865): a staff member entering an organisation's
 * workspace needs a seat. Switched off (the platform setting `licence_web_check`, off until go-live) the database
 * answers "off" and everyone goes straight in, as before. Switched on, a seat is taken when one is free; with none
 * free the person sees who holds them, and a tenant admin can release one here. An organisation whose licence is
 * cancelled or was never set up is refused (decision 865): its tenant admins see only the Licences page's licence and
 * invoices. A check that could not be made never locks anyone out. Clients, Preview as client (the staff member was
 * checked first) and Braynewave's console are never checked.
 */
/** The refusal's title when the organisation's licence is cancelled or was never set up (decision 865). */
export function endedTitle(reason: 'no_licence' | 'tenant_cancelled', tenantName: string): string {
  return reason === 'tenant_cancelled'
    ? `${tenantName}’s Teslyn licence has ended`
    : `${tenantName} has no Teslyn licence yet`;
}

/** What the refused person reads; a tenant admin also sees the licence and invoices under it. */
export function endedWords(tenantName: string, admin: boolean): string {
  return admin
    ? `Staff cannot sign in to Teslyn Cloud for ${tenantName} until Braynewave sets up the licence again. Nothing is deleted. Your licence and Braynewave’s invoices are below; contact Braynewave.`
    : `Staff cannot sign in to Teslyn Cloud for ${tenantName} until Braynewave sets up the licence again. Nothing is deleted. Ask your tenant admin to contact Braynewave.`;
}

export function LicenceGate({
  sb,
  tenant,
  tenantId,
  tenantName,
  roles,
  onSignOut,
  onSwitch,
  children,
}: LicenceGateProps): ReactElement {
  const [gate, setGate] = useState<WebSignInGate | null>(null);
  const check = useCallback(() => {
    setGate(null);
    void webCheckin(sb, tenantId).then((a) => setGate(webSignInGate(a)));
  }, [sb, tenantId]);
  useEffect(check, [check]);

  if (!gate) {
    return (
      <div className="signin">
        <Loading what="your licence" />
      </div>
    );
  }
  if (gate.kind === 'open') return <>{children}</>;

  const admin = canManageLicences(roles);
  return (
    <div className="signin">
      <main className="signin__card signin__card--wide" aria-labelledby="licence-gate-title">
        {tenant.logo ? (
          <img className="signin__logo" src={tenant.logo} alt={tenant.name} />
        ) : (
          <span className="heading">{tenant.name}</span>
        )}
        <h1 id="licence-gate-title" className="title">
          {gate.kind === 'no_seat' ? 'No free seat' : endedTitle(gate.reason, tenantName)}
        </h1>
        {gate.kind === 'no_seat' ? (
          <>
            <p className="body">
              {`${gate.inUse} of ${gate.seats === 1 ? '1 seat' : `${gate.seats} seats`} of ${tenantName}’s licence are in use, so you cannot take one now.`}{' '}
              A tenant admin can release a seat in Teslyn Cloud, Company, Licences; then choose Try again.
              Braynewave can add seats.
            </p>
            <h2 className="heading">Who holds a seat</h2>
            <ul className="stack" aria-label="Seat holders">
              {gate.holders.map((h) => (
                <li key={h.userId} className="body">
                  {h.name}
                  {h.email && <span className="caption muted data"> {h.email}</span>}
                  <span className="caption muted"> · last checked in {daysAgoText(h.daysSinceCheckin)}</span>
                </li>
              ))}
            </ul>
            {admin && <LicencesScreen sb={sb} tenantId={tenantId} tenantName={tenantName} embedded />}
          </>
        ) : (
          <>
            <p className="body">{endedWords(tenantName, admin)}</p>
            {admin && (
              <LicencesScreen
                sb={sb}
                tenantId={tenantId}
                tenantName={tenantName}
                embedded
                showSeats={false}
                showInvoices
              />
            )}
          </>
        )}
        <div className="row">
          <Button variant="primary" onClick={check}>
            Try again
          </Button>
          {onSwitch && (
            <Button variant="quiet" onClick={onSwitch}>
              Switch organisation
            </Button>
          )}
          <Button variant="quiet" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </main>
      <footer className="signin__foot">
        <PoweredBy />
      </footer>
    </div>
  );
}
